/* ------------------------------------------------------------------
   ATHR behaviour overrides. Hand-written, not part of Kalles.
   Loaded as a module from layout/theme.liquid.
   ------------------------------------------------------------------ */

/* The sticky bar's button says "Buy now", so it has to actually buy:
   add the line and go straight to checkout, rather than Kalles' default
   of adding it and sliding the cart drawer open.

   Kalles wires the add-to-cart XHR on <wrapp-hdt-pr-frm>, which listens
   for submit in the bubble phase. Listening in the CAPTURE phase and
   calling stopImmediatePropagation means that handler never runs, so
   there is no drawer to fight with. */
document.addEventListener('submit', function (event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.classList.contains('hdt-sticky-atc__form')) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const button = form.querySelector('[type="submit"][name="add"]');
  if (button) {
    if (button.getAttribute('disabled') === 'true' || button.disabled) return;
    button.setAttribute('disabled', 'true');
    button.setAttribute('aria-busy', 'true');
  }

  const body = new FormData(form);
  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: body
  })
    .then((response) => response.json())
    .then((result) => {
      if (result.status) throw new Error(result.description || result.message);
      window.location.href = window.Shopify.routes.root + 'checkout';
    })
    .catch((error) => {
      console.error('[ATHR] buy now failed, falling back to the cart', error);
      if (button) {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }
      window.location.href = window.Shopify.routes.root + 'cart';
    });
}, true);

/* "Continue shopping" in the cart drawer. Kalles only binds its own
   [ref="closeButton"], and a second button carrying aria-controls does
   not close an already-open drawer, so hand the click to the real close
   button and let the theme do its own scroll-unlock and focus return. */
document.addEventListener('click', function (event) {
  const button = event.target.closest('.hdt-mini-cart__btn-viewcart');
  if (!button) return;
  event.preventDefault();
  const closeButton = button.closest('dialog')?.querySelector('[ref="closeButton"]');
  if (closeButton) closeButton.click();
});

/* Take the announcement marquee over from Kalles.

   Two problems with the theme's own version (Marquee in
   assets/global.min.js), and one fix covers both:

   1. It scrolls by scrollWidth / 2, which is not a whole number of
      message widths, so the strip hitches ~30px every cycle when the
      animation restarts. That one affects every visitor.

   2. It returns before creating the animation at all when
      '(prefers-reduced-motion: no-preference)' does not match. The
      element keeps overflow-x: scroll, so with Reduce Motion on the bar
      could only be dragged by hand, sitting truncated at the screen edge.

   Animating by exactly one message pitch is seamless, because every copy
   is identical: ending one pitch along is pixel-for-pixel the start. The
   theme already clones enough copies to cover the viewport twice over, so
   there is always more content than the window can show.

   Speed and direction are read back off the element's own attributes, so
   the theme editor's marquee settings still drive this. */
(function announcementMarquee() {
  const REDUCED_MAX_SPEED = 75; // px/s ceiling when Reduce Motion is on
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let animation = null;
  let resizeObserver = null;
  let mutationObserver = null;
  let frame = null;

  function start() {
    const marquee = document.querySelector('.hdt-announcement-bar hdt-marquee');
    const inner = marquee?.querySelector('.hdt-marquee-inner');
    if (!inner) return;

    /* Measure before touching anything. If the theme has not cloned yet
       there is nothing to animate, and bailing out here leaves whatever is
       already running alone rather than cancelling it and stopping the bar. */
    const items = inner.children;
    if (items.length < 2) return;

    /* Distance between the start of one copy and the start of the next,
       measured rather than derived, so it holds whatever the gap is set to.
       Deliberately not offsetLeft: that rounds to whole pixels, and a
       fractional pitch (669.2 + a 60px gap here) would then lose a fraction
       every cycle and slowly reintroduce the drift this is meant to remove.
       Both rects carry the running transform equally, so it cancels out. */
    const pitch =
      items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left;
    if (pitch <= 0) return;

    /* Now clear the track. Depending on whether the theme's ResizeObserver
       re-ran, that is either its animation or our own from the last pass. */
    inner.getAnimations().forEach((existing) => existing.cancel());
    animation = null;

    /* Kalles multiplies its speed-factor setting by 300 to get px/s. */
    const factor = parseFloat(marquee.getAttribute('speed-factor')) || 0.4;
    const speed = reduced.matches
      ? Math.min(REDUCED_MAX_SPEED, factor * 300)
      : factor * 300;

    /* Same flip the theme applies: on an RTL page the setting is mirrored. */
    const setting = marquee.getAttribute('direction') || 'ltr';
    const pageIsRTL = document.documentElement.getAttribute('dir') === 'rtl';
    const direction = pageIsRTL ? (setting === 'ltr' ? 'rtl' : 'ltr') : setting;

    const from = direction === 'rtl' ? 0 : -pitch;
    const to = direction === 'rtl' ? -pitch : 0;

    animation = inner.animate(
      [
        { transform: `translateX(${from}px) translateZ(0)` },
        { transform: `translateX(${to}px) translateZ(0)` }
      ],
      { duration: (pitch / speed) * 1000, easing: 'linear', iterations: Infinity }
    );
  }

  /* The theme rebuilds its clones from its own ResizeObserver, which moves
     the pitch under us. Wait a frame so we measure the rebuilt track, and
     so our animation is always the last one applied. */
  function restart() {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = null;
      start();
    });
  }

  function attach() {
    const marquee = document.querySelector('.hdt-announcement-bar hdt-marquee');
    if (!marquee) return;

    restart();

    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(restart);
    resizeObserver.observe(marquee);

    /* The clones are what we measure, and appending them does not resize
       the marquee, so the ResizeObserver above never sees them arrive. Watch
       the track itself: this fires both on first paint and every time the
       theme tears the clones down and rebuilds them on resize. */
    const inner = marquee.querySelector('.hdt-marquee-inner');
    if (inner) {
      mutationObserver?.disconnect();
      mutationObserver = new MutationObserver(restart);
      mutationObserver.observe(inner, { childList: true });
    }

    /* The section markup hard-codes `pausable`. The theme's own listeners
       now pause an animation we cancelled, so carry the behaviour here. */
    if (!marquee.dataset.athrPausable) {
      marquee.dataset.athrPausable = 'true';
      marquee.addEventListener('mouseenter', () => animation?.pause());
      marquee.addEventListener('mouseleave', () => animation?.play());
    }
  }

  customElements.whenDefined('hdt-marquee').then(attach);
  reduced.addEventListener('change', restart);
  /* Re-attach when the theme editor re-renders the header group. */
  document.addEventListener('shopify:section:load', () => {
    customElements.whenDefined('hdt-marquee').then(attach);
  });
})();

/* Keep the product preview active while a finger scrolls across the grid.
   CSS :active ends as soon as the browser starts scrolling, so the class is
   managed from passive touch listeners instead of blocking the page gesture. */
(function touchProductPreview() {
  let previewMedia = null;

  function mediaFromTarget(target) {
    return target instanceof Element
      ? target.closest('.hdt-card-product__media')
      : null;
  }

  function showPreview(media) {
    if (previewMedia === media) return;
    previewMedia?.classList.remove('athr-touch-preview');
    previewMedia = media;
    previewMedia?.classList.add('athr-touch-preview');
  }

  function clearPreview() {
    previewMedia?.classList.remove('athr-touch-preview');
    previewMedia = null;
  }

  function updatePreview(touch) {
    showPreview(mediaFromTarget(document.elementFromPoint(touch.clientX, touch.clientY)));
  }

  document.addEventListener('touchstart', (event) => {
    const touch = event.touches[0];
    if (touch) showPreview(mediaFromTarget(event.target));
  }, { passive: true });

  document.addEventListener('touchmove', (event) => {
    const touch = event.touches[0];
    if (touch) updatePreview(touch);
  }, { passive: true });

  document.addEventListener('touchend', clearPreview, { passive: true });
  document.addEventListener('touchcancel', clearPreview, { passive: true });
  window.addEventListener('blur', clearPreview);
})();

/* Free 5 ml tester choice (snippets/athr-tester-picker.liquid).
   The choice is a cart attribute, "Free tester", so it travels with the
   cart into checkout and onto the order. Every picker on the page (cart
   drawer, cart page, product form) mirrors the same value. */
(() => {
  const KEY = 'Free tester';
  const save = (value) =>
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [KEY]: value } })
    }).catch((error) => console.error('[ATHR] could not save the tester choice', error));

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-athr-tester-select]');
    if (!select) return;
    document.querySelectorAll('[data-athr-tester-select]').forEach((other) => {
      if (other !== select) other.value = select.value;
    });
    save(select.value);
  });

  /* The drawer is re-rendered from the server after every cart change, and
     the product form can load before the cart has an attribute at all, so
     re-read the real value whenever a picker appears. */
  const sync = () =>
    fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const value = (cart.attributes && cart.attributes[KEY]) || 'Surprise me';
        document.querySelectorAll('[data-athr-tester-select]').forEach((select) => {
          if ([...select.options].some((o) => o.value === value && !o.disabled)) select.value = value;
        });
      })
      .catch(() => {});
  if (document.querySelector('[data-athr-tester-select]')) sync();
  new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-athr-tester]') || n.querySelector?.('[data-athr-tester]'))))) sync();
  }).observe(document.body, { childList: true, subtree: true });

  /* Shopify's "Buy it now" button checks out the one product directly and
     ignores cart attributes, which would lose the tester choice. Send it
     through the cart instead, the same way the sticky "Buy now" works. */
  document.addEventListener('click', (event) => {
    const wrap = event.target.closest?.('.shopify-payment-button');
    if (!wrap) return;
    const form = wrap.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status) throw new Error(result.description || result.message);
        window.location.href = window.Shopify.routes.root + 'checkout';
      })
      .catch((error) => {
        console.error('[ATHR] buy it now failed, falling back to the cart', error);
        window.location.href = window.Shopify.routes.root + 'cart';
      });
  }, true);
})();
